import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { DIMENSIONS } from './constants';
import { toAtom } from '/src/utils/string';

export const webAnalyticsFeatureKeys = createQueryKeys('webAnalyticsFeature', {
  analytics: ({
    view,
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    granularity,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }) => [
    {
      view,
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
      granularity,
      searchTerm,
      orderBy,
      orderDir,
      limit,
      cursor,
    },
  ],
  analyticsDimension: ({
    dimension,
    view,
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }) => [
    {
      dimension,
      view,
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
      searchTerm,
      orderBy,
      orderDir,
      limit,
      cursor,
    },
  ],
  overTime: ({ dimension, metric, sourceId, sourceType, startAt, endAt, presetDate, numSegments, goalId }) => [
    {
      dimension,
      metric,
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
      numSegments,
      goalId,
    },
  ],
  analyticsForHomeDash: ({
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }) => [
    {
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
      searchTerm,
      orderBy,
      orderDir,
      limit,
      cursor,
    },
  ],
  conversionsOverTime: ({ goalId, sourceId, sourceType, startAt, endAt, presetDate }) => [
    {
      goalId,
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
    },
  ],
});

const metricOverTimeFragment = gql`
  fragment metricOverTime on MetricOverTime {
    total
    delta
    bucket {
      startDate
      endDate
      value
    }
  }
`;

const categoricalMetricFragment = gql`
  fragment categoricalMetric on CategoricalMetric {
    name
    code
    domain
    visitors
    pageViews
    sessions
    avgSessionDuration
    avgTimeOnPage
    bounceRate
    favicon
  }
`;

const categoricalMetricOverTimeFragment = gql`
  fragment categoricalMetricOverTime on CategoricalMetricOverTime {
    name
    domain
    dailyData {
      date
      value
      prevValue
      pctChange
    }
  }
`;

const chartFields = `
  # Values for the chart
  uniqueVisitors {
    ...metricOverTime
  }
  pageViews {
    ...metricOverTime
  }
  avgSessionDuration {
    ...metricOverTime
  }
  sessions {
    ...metricOverTime
  }
  bounceRate {
    ...metricOverTime
  }
`;

const visitorsField = `
  visitors {
    new
    returning
    total
  }
`;

function fieldQuery(metric, options) {
  const queryCount = options?.queryCount ?? true;
  const queryOverTime = options?.overTime ?? false;
  const queryData = options?.queryData ?? true;
  const goalId = options?.goalId ?? false;

  return `
  ${metric} ${queryData ? `(limit: $limit)` : ``} {
    ${queryCount ? `totalCount` : ''}
    ${
      queryOverTime
        ? `
      overTime {
        ...categoricalMetricOverTime
      }
    `
        : ''
    }
    ${
      queryData
        ? `
      data {
        ...categoricalMetric
      }
    `
        : ''
    }
    ${
      goalId
        ? `
      conversions {
        date
        value
      }
    `
        : ''
    }
  }
`;
}

function dashboardFieldQuery(metric) {
  return `
  ${metric} (limit: $limit) {
    totalCount
    data {
      ...categoricalMetric
    }
  }
`;
}

function maybeQueryField(view, metric, options) {
  if (view === 'ALL') {
    return dashboardFieldQuery(metric);
  }
  if (view === metric) {
    return fieldQuery(metric, options);
  }
  return '';
}

function fieldCursor(view) {
  if (!view || view === 'ALL') return '';
  if (
    [
      DIMENSIONS.DEVICE_TYPE,
      DIMENSIONS.COUNTRIES,
      DIMENSIONS.LANGUAGES,
      DIMENSIONS.OS,
      DIMENSIONS.BROWSERS,
      DIMENSIONS.CHANNELS,
    ].includes(view)
  )
    return '';
  return `
    # Pagination cursor
    nextCursor {
      ${view.replace(/s$/g, '')} {
    key
    value
  }
}
`;
}

const DEFAULT_LIMIT = 250;

export const webAnalyticsDimensionQuery = ({
  dimension,
  sourceId,
  sourceType,
  startAt,
  endAt,
  presetDate,
  searchTerm,
  orderBy,
  orderDir,
  limit,
  cursor,
}) => ({
  ...webAnalyticsFeatureKeys.analyticsDimension({
    dimension,
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }),
  useErrorBoundary: false,
  getNextPageParam: (lastPage, _pages) => {
    const rows = lastPage?.analyticsDimension?.dimension?.data ?? [];
    if (rows.length < DEFAULT_LIMIT) return undefined;
    return lastPage.analyticsDimension.nextCursor;
  },
  meta: {
    query: gql`
      query WebAnalyticsDimension(
        $dimension: AnalyticsDimension!
        $sourceId: Int!
        $sourceType: AnalyticsSource!
        $startAt: Int
        $endAt: Int
        $presetDate: String
        $searchTerm: String
        $orderBy: OrderColumn
        $orderDir: OrderDir
        $limit: Int
        $cursor: AnalyticsCursorDataArg
      ) {
        analyticsDimension(
          dimension: $dimension
          sourceId: $sourceId
          sourceType: $sourceType
          startAt: $startAt
          endAt: $endAt
          presetDate: $presetDate
          searchTerm: $searchTerm
          orderBy: $orderBy
          orderDir: $orderDir
          limit: $limit
          cursor: $cursor
        ) {
          nextCursor {
            key
            value
          }
          dimension {
            totalCount
            data {
              ...categoricalMetric
            }
          }
        }
      }

      ${categoricalMetricFragment}
    `,
  },
});

export const webAnalyticsQuery = ({
  sourceId,
  sourceType,
  startAt,
  endAt,
  presetDate,
  granularity,
  searchTerm,
  orderBy,
  orderDir,
  view = 'ALL',
  limit,
  cursor = {},
}) => ({
  ...webAnalyticsFeatureKeys.analytics({
    view,
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    granularity,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }),
  useErrorBoundary: false,
  getNextPageParam: (lastPage, _pages) => {
    if (view === 'ALL') return undefined;
    const rows = lastPage?.analytics?.[view]?.data ?? [];
    if (rows.length < DEFAULT_LIMIT) return undefined;
    return lastPage.analytics.nextCursor;
  },
  meta: {
    query: gql`
      query WebAnalytics($sourceId: Int!, $sourceType: AnalyticsSource!, $startAt: Int, $endAt: Int, $presetDate: String, $searchTerm: String, $orderBy: OrderColumn, $orderDir: OrderDir, $limit: Int, $cursor: AnalyticsCursorArg, $granularity: TimeUnit) {
  analytics(sourceId: $sourceId, sourceType: $sourceType, startAt: $startAt, endAt: $endAt, presetDate: $presetDate, searchTerm: $searchTerm, orderBy: $orderBy, orderDir: $orderDir, cursor: $cursor, granularity: $granularity) {
          ${fieldCursor(view)}
          ${view === 'ALL' ? chartFields : ''}
          # Values for the everything else
          ${view === 'ALL' ? visitorsField : ''}
          ${maybeQueryField(view, 'deviceType')}
          ${maybeQueryField(view, 'countries')}
          ${maybeQueryField(view, 'languages')}
          ${maybeQueryField(view, 'os')}
          ${maybeQueryField(view, 'browsers')}
          ${maybeQueryField(view, 'channels')}
          ${maybeQueryField(view, 'referrers')}
          ${maybeQueryField(view, 'pages')}
          ${maybeQueryField(view, 'entryPages')}
          ${maybeQueryField(view, 'utmCampaigns')}
          ${maybeQueryField(view, 'utmSources')}
          ${maybeQueryField(view, 'utmMediums')}
          ${maybeQueryField(view, 'utmTerms')}
          ${maybeQueryField(view, 'utmContents')}
  }
}

      ${view === 'ALL' ? metricOverTimeFragment : ''}
      ${categoricalMetricFragment}
`,
  },
});

export const webAnalyticsOverTimeQuery = ({
  dimension,
  metric,
  sourceId,
  sourceType,
  startAt,
  endAt,
  presetDate,
  numSegments,
  goalId,
}) => {
  const parsedDimension = toAtom(dimension);

  return {
    ...webAnalyticsFeatureKeys.overTime({
      dimension: parsedDimension,
      metric,
      sourceId,
      sourceType,
      startAt,
      endAt,
      presetDate,
      numSegments,
      goalId,
    }),
    useErrorBoundary: false,
    meta: {
      query: gql`
      query WebAnalyticsOverTime($dimension: AnalyticsDimension!, $metric: AnalyticsMetric!, $sourceId: Int!, $sourceType: AnalyticsSource!, $startAt: Int, $endAt: Int, $presetDate: String, $numSegments: Int, $goalId: Int) {
  analyticsOverTime(dimension: $dimension, metric: $metric, sourceId: $sourceId, sourceType: $sourceType, startAt: $startAt, endAt: $endAt, presetDate: $presetDate, numSegments: $numSegments, goalId: $goalId) {
    ${fieldQuery(dimension, { queryCount: false, queryData: false, overTime: true, goalId: !!goalId })}
  }
}

      ${categoricalMetricOverTimeFragment}
`,
    },
  };
};

export const webAnalyticsConversionsOverTimeQuery = ({ goalId, sourceId, sourceType, startAt, endAt, presetDate }) => ({
  ...webAnalyticsFeatureKeys.conversionsOverTime({
    goalId,
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query WebAnalyticsConversionsOverTime(
        $goalId: Int
        $sourceId: Int!
        $sourceType: AnalyticsSource!
        $startAt: Int
        $endAt: Int
        $presetDate: String
      ) {
        analyticsConversionsOverTime(
          goalId: $goalId
          sourceId: $sourceId
          sourceType: $sourceType
          startAt: $startAt
          endAt: $endAt
          presetDate: $presetDate
        ) {
          date
          value
        }
      }
    `,
  },
});

export const webAnalyticsQueryForHomeDash = ({
  sourceId,
  sourceType,
  startAt,
  endAt,
  presetDate = null,
  searchTerm = undefined,
  orderBy = undefined,
  orderDir = undefined,
  limit = 3,
  cursor = {},
}) => ({
  ...webAnalyticsFeatureKeys.analyticsForHomeDash({
    sourceId,
    sourceType,
    startAt,
    endAt,
    presetDate,
    searchTerm,
    orderBy,
    orderDir,
    limit,
    cursor,
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query WebAnalyticsHomeDash(
        $sourceId: Int!
        $sourceType: AnalyticsSource!
        $startAt: Int
        $endAt: Int
        $presetDate: String
        $searchTerm: String
        $orderBy: OrderColumn
        $orderDir: OrderDir
        $limit: Int
        $cursor: AnalyticsCursorArg
      ) {
        analytics(
          sourceId: $sourceId
          sourceType: $sourceType
          startAt: $startAt
          endAt: $endAt
          presetDate: $presetDate
          searchTerm: $searchTerm
          orderBy: $orderBy
          orderDir: $orderDir
          cursor: $cursor
        ) {
          uniqueVisitors {
            total
            delta
          }
          avgSessionDuration {
            total
            delta
          }
          bounceRate {
            total
            delta
          }

          channels(limit: $limit) {
            data {
              name
              visitors
            }
          }
          entryPages(limit: $limit) {
            data {
              domain
              name
              visitors
            }
          }
        }
      }
    `,
  },
});
